<template>
  <div>
    <LayoutPageHeader 
      title="Developer Resources" 
      background-image="https://cdn-media.geekcafe.com/a0d4d17b-ec8f-4f28-9169-97e3e5bb4d37/person-looking-at-phone-and-at-macbook-pro-1181244_1920x400.jpg" 
    />
    
    <div class="container resources-page">
      <div class="resources-intro">
        <p>
          A curated collection of essential resources, documentation, and tools for cloud development, 
          AWS services, and modern application architecture. These are the resources we use and recommend 
          for building scalable, secure, and efficient cloud solutions.
        </p>
      </div>

      <!-- Category Filter -->
      <div class="category-filter">
        <button 
          v-for="category in categories" 
          :key="category"
          :class="['filter-btn', { active: selectedCategory === category }]"
          @click="selectedCategory = category"
        >
          {{ category }}
        </button>
      </div>

      <!-- Resources Grid -->
      <div class="resources-grid">
        <div 
          v-for="resource in filteredResources" 
          :key="resource.id"
          class="resource-card"
          @click="openResource(resource.url)"
        >
          <div class="card-header">
            <div class="icon-wrapper" :class="`icon-${resource.icon}`">
              <!-- Cloud -->
              <svg v-if="resource.icon === 'cloud'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
              </svg>
              <!-- Code -->
              <svg v-else-if="resource.icon === 'code'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="16 18 22 12 16 6" />
                <polyline points="8 6 2 12 8 18" />
              </svg>
              <!-- Book -->
              <svg v-else-if="resource.icon === 'book'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
              <!-- Database -->
              <svg v-else-if="resource.icon === 'database'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <ellipse cx="12" cy="5" rx="9" ry="3" />
                <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
              </svg>
              <!-- Layers -->
              <svg v-else-if="resource.icon === 'layers'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polygon points="12 2 2 7 12 12 22 7 12 2" />
                <polyline points="2 17 12 22 22 17" />
                <polyline points="2 12 12 17 22 12" />
              </svg>
              <!-- Zap -->
              <svg v-else-if="resource.icon === 'zap'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
              </svg>
              <!-- Git -->
              <svg v-else-if="resource.icon === 'git'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22" />
              </svg>
              <!-- Graduation Cap -->
              <svg v-else-if="resource.icon === 'graduation-cap'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                <path d="M6 12v5c3 3 9 3 12 0v-5" />
              </svg>
              <!-- Shield -->
              <svg v-else-if="resource.icon === 'shield'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <!-- Users -->
              <svg v-else-if="resource.icon === 'users'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <!-- CPU/Brain -->
              <svg v-else-if="resource.icon === 'cpu'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
                <rect x="9" y="9" width="6" height="6" />
                <line x1="9" y1="1" x2="9" y2="4" />
                <line x1="15" y1="1" x2="15" y2="4" />
                <line x1="9" y1="20" x2="9" y2="23" />
                <line x1="15" y1="20" x2="15" y2="23" />
                <line x1="20" y1="9" x2="23" y2="9" />
                <line x1="20" y1="14" x2="23" y2="14" />
                <line x1="1" y1="9" x2="4" y2="9" />
                <line x1="1" y1="14" x2="4" y2="14" />
              </svg>
              <!-- Award -->
              <svg v-else-if="resource.icon === 'award'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="8" r="7" />
                <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
              </svg>
              <!-- Video -->
              <svg v-else-if="resource.icon === 'video'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polygon points="23 7 16 12 23 17 23 7" />
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
              </svg>
              <!-- Terminal -->
              <svg v-else-if="resource.icon === 'terminal'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="4 17 10 11 4 5" />
                <line x1="12" y1="19" x2="20" y2="19" />
              </svg>
              <!-- Kanban -->
              <svg v-else-if="resource.icon === 'kanban'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
              </svg>
              <!-- File Text -->
              <svg v-else-if="resource.icon === 'file-text'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
              <!-- Check Square -->
              <svg v-else-if="resource.icon === 'check-square'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="9 11 12 14 22 4" />
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
              </svg>
              <!-- Layout -->
              <svg v-else-if="resource.icon === 'layout'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="3" y1="9" x2="21" y2="9" />
                <line x1="9" y1="21" x2="9" y2="9" />
              </svg>
              <!-- Message Square -->
              <svg v-else-if="resource.icon === 'message-square'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              <!-- Image -->
              <svg v-else-if="resource.icon === 'image'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              <!-- Globe -->
              <svg v-else-if="resource.icon === 'globe'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              <!-- Activity -->
              <svg v-else-if="resource.icon === 'activity'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
              <!-- Git Branch -->
              <svg v-else-if="resource.icon === 'git-branch'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="6" y1="3" x2="6" y2="15" />
                <circle cx="18" cy="6" r="3" />
                <circle cx="6" cy="18" r="3" />
                <path d="M18 9a9 9 0 0 1-9 9" />
              </svg>
              <!-- Share 2 -->
              <svg v-else-if="resource.icon === 'share-2'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
              <!-- Package -->
              <svg v-else-if="resource.icon === 'package'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                <line x1="12" y1="22.08" x2="12" y2="12" />
              </svg>
              <!-- Tool -->
              <svg v-else-if="resource.icon === 'tool'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
              </svg>
              <!-- Default Book icon -->
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
            </div>
            <div class="category-badge">{{ resource.category }}</div>
          </div>
          <div class="card-body">
            <h3 class="resource-title">{{ resource.title }}</h3>
            <p class="resource-description">{{ resource.description }}</p>
          </div>
          <div class="card-footer">
            <a 
              :href="resource.url" 
              target="_blank" 
              rel="noopener noreferrer"
              class="resource-link"
            >
              Visit Resource
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="filteredResources.length === 0" class="empty-state">
        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <p>No resources found in this category.</p>
      </div>

      <!-- CTA Section -->
      <div class="cta-section">
        <div class="cta-content">
          <h2>Have a resource to suggest?</h2>
          <p>Know of a great tool or documentation we should add? Let us know!</p>
          <button class="btn btn-primary" @click="openContactChat('Resource Suggestion')">Suggest a Resource</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import resourcesData from '~/config/resources.json'

const { openChat: openContactChat } = useContactChat()

useHead({
  title: 'Developer Resources | Geek Cafe',
  meta: [
    { name: 'description', content: 'Curated resources for AWS, cloud development, Python, and modern application architecture. Essential documentation and tools for building scalable cloud solutions.' }
  ]
})

interface Resource {
  id: number
  title: string
  description: string
  url: string
  category: string
  icon: string
}

const resources = ref<Resource[]>(resourcesData)
const selectedCategory = ref('All')

const categories = computed(() => {
  const cats = ['All', ...new Set(resources.value.map(r => r.category))]
  return cats.sort()
})

const filteredResources = computed(() => {
  if (selectedCategory.value === 'All') {
    return resources.value
  }
  return resources.value.filter(r => r.category === selectedCategory.value)
})

const openResource = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer')
}
</script>

<style scoped>
.resources-page {
  padding: 4rem 0;
  max-width: 1200px;
}

.resources-intro {
  text-align: center;
  margin-bottom: 3rem;
}

.resources-intro p {
  font-size: 1.1rem;
  color: #4a5568;
  line-height: 1.7;
  max-width: 800px;
  margin: 0 auto;
}

.category-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
  margin-bottom: 3rem;
}

.filter-btn {
  background: white;
  border: 2px solid #e2e8f0;
  padding: 0.625rem 1.25rem;
  border-radius: 24px;
  font-weight: 600;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.95rem;
}

.filter-btn:hover {
  border-color: #667eea;
  color: #667eea;
  background: #f7fafc;
}

.filter-btn.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-color: transparent;
}

.resources-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
}

.resource-card {
  background: white;
  border-radius: 16px;
  border: 2px solid #e2e8f0;
  overflow: hidden;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  cursor: pointer;
}

.resource-card:hover {
  border-color: #667eea;
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(102, 126, 234, 0.15);
}

.card-header {
  padding: 1.5rem 1.5rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.icon-cloud { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
.icon-code { background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); }
.icon-book { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); }
.icon-database { background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%); }
.icon-layers { background: linear-gradient(135deg, #fa709a 0%, #fee140 100%); }
.icon-zap { background: linear-gradient(135deg, #30cfd0 0%, #330867 100%); }
.icon-git { background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%); }
.icon-graduation-cap { background: linear-gradient(135deg, #ff6e7f 0%, #bfe9ff 100%); }
.icon-shield { background: linear-gradient(135deg, #ee0979 0%, #ff6a00 100%); }
.icon-users { background: linear-gradient(135deg, #56ab2f 0%, #a8e063 100%); }
.icon-cpu { background: linear-gradient(135deg, #3f51b5 0%, #5a55ae 100%); }
.icon-award { background: linear-gradient(135deg, #ffd89b 0%, #19547b 100%); }
.icon-video { background: linear-gradient(135deg, #de6161 0%, #2657eb 100%); }
.icon-terminal { background: linear-gradient(135deg, #000000 0%, #434343 100%); }
.icon-kanban { background: linear-gradient(135deg, #f857a6 0%, #ff5858 100%); }
.icon-file-text { background: linear-gradient(135deg, #48c6ef 0%, #6f86d6 100%); }
.icon-check-square { background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%); }
.icon-layout { background: linear-gradient(135deg, #fc466b 0%, #3f5efb 100%); }
.icon-message-square { background: linear-gradient(135deg, #5f2c82 0%, #49a09d 100%); }
.icon-image { background: linear-gradient(135deg, #ec008c 0%, #fc6767 100%); }
.icon-globe { background: linear-gradient(135deg, #00d2ff 0%, #3a7bd5 100%); }
.icon-activity { background: linear-gradient(135deg, #f2709c 0%, #ff9472 100%); }
.icon-git-branch { background: linear-gradient(135deg, #8e2de2 0%, #4a00e0 100%); }
.icon-share-2 { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
.icon-package { background: linear-gradient(135deg, #f6d365 0%, #fda085 100%); }
.icon-tool { background: linear-gradient(135deg, #ff9966 0%, #ff5e62 100%); }

.category-badge {
  background: #edf2f7;
  color: #4a5568;
  padding: 0.375rem 0.875rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.card-body {
  padding: 0 1.5rem 1.5rem;
  flex: 1;
}

.resource-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.75rem;
}

.resource-description {
  color: #4a5568;
  line-height: 1.6;
  margin: 0;
}

.card-footer {
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #f7fafc;
}

.resource-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #667eea;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
}

.resource-link:hover {
  color: #764ba2;
  gap: 0.75rem;
}

.resource-link svg {
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.resource-link:hover svg {
  transform: translateX(2px);
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #a0aec0;
}

.empty-state svg {
  margin-bottom: 1.5rem;
  opacity: 0.4;
}

.empty-state p {
  font-size: 1.1rem;
  color: #718096;
}

.cta-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 3rem 2rem;
  text-align: center;
  margin-top: 3rem;
}

.cta-content h2 {
  color: white;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.cta-content p {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  margin-bottom: 2rem;
}

.btn-primary {
  background: white;
  color: #667eea;
  border: none;
  padding: 0.875rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

@media (max-width: 768px) {
  .resources-page {
    padding: 2rem 0;
  }

  .resources-intro p {
    font-size: 1rem;
  }

  .resources-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .category-filter {
    gap: 0.5rem;
  }

  .filter-btn {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
  }

  .cta-content h2 {
    font-size: 1.5rem;
  }

  .cta-content p {
    font-size: 1rem;
  }
}
</style>
